import { useCallback, useEffect, useRef, useState } from "react"

import { useRecoilState, useRecoilValue } from "recoil"

import { transpileTypeScript } from "../../../pages/plugins/utils/codeTranspiler"
import { activeFileSelector } from "../selectors"

/**
 * Hook to handle TypeScript transpilation logic
 * This separates transpilation concerns from editor integration
 */
export function useCodeTranspiler() {
  // Get references to Recoil state
  const activeFile = useRecoilValue(activeFileSelector)
  const [_, setActiveFile] = useRecoilState(activeFileSelector)

  // Local UI state for transpilation status
  const [status, setStatus] = useState<
    "idle" | "processing" | "updated" | "error"
  >("idle")
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // The core transpile function
  const transpileCode = useCallback(
    async (code: string) => {
      if (!activeFile) return

      setStatus("processing")
      console.log("[Transpile] Transpiling code:", code.slice(0, 50) + "...")

      try {
        // Call the transpiler service
        const result = await transpileTypeScript(code)

        if (!result.success) {
          console.error("[Transpile] Error:", result.error || "Unknown error")
          setStatus("error")
          // Reset status after a delay
          setTimeout(() => setStatus("idle"), 1500)
          return
        }

        // Update Recoil state with transpiled code
        setActiveFile({
          transpiledContent: result.transpiledCode,
        } as any)

        console.log("[Transpile] Transpilation successful")

        // Show success status
        setStatus("updated")
        setTimeout(() => setStatus("idle"), 1500)

        return result.transpiledCode
      } catch (error) {
        console.error("[Transpile] Error during transpilation:", error)
        setStatus("error")
        // Reset status after a delay
        setTimeout(() => setStatus("idle"), 1500)
      }
    },
    [activeFile, setActiveFile],
  )

  // Debounced transpile function for editor changes
  const debouncedTranspile = useCallback(
    (code: string) => {
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Set status to processing for UI feedback
      setStatus("processing")

      // Set a new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        transpileCode(code)
        debounceTimerRef.current = null
      }, 2000)
    },
    [transpileCode],
  )

  // Auto-transpile effect: when code changes but no transpiled code exists
  useEffect(() => {
    if (
      activeFile?.content &&
      (!activeFile.transpiledContent ||
        activeFile.transpiledContent.trim() === "") &&
      status === "idle"
    ) {
      console.log("[AutoTranspile] Transpiling file without transpiled content")
      transpileCode(activeFile.content)
    }
  }, [
    activeFile?.id,
    activeFile?.content,
    activeFile?.transpiledContent,
    status,
    transpileCode,
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    transpileStatus: status,
    transpileCode,
    debouncedTranspile,
    cancelTranspile: () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
        setStatus("idle")
      }
    },
  }
}
